import { useState } from "react";
import {
  Banknote,
  Building2,
  ChevronDown,
  ChevronUp,
  CreditCard,
  DollarSign,
  HelpCircle,
  PauseCircle,
  TrendingDown,
  TrendingUp,
  Wallet,
} from "lucide-react";
import ReactCountryFlag from "react-country-flag";

import { cn } from "@/utils/cn";
import type { ITotalByCurrency } from "@/services/(company)/payrollRunsApi";
import type { IPaymentMethodBreakdown } from "@/types/(company)/payrollRuns.interface";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

const CURRENCY_TO_COUNTRY: Record<string, string> = {
  AED: "AE",
  USD: "US",
  GBP: "GB",
  EUR: "EU",
  KES: "KE",
  INR: "IN",
  PKR: "PK",
  SAR: "SA",
  EGP: "EG",
  JOD: "JO",
  BHD: "BH",
  KWD: "KW",
  OMR: "OM",
  QAR: "QA",
};

const toCountryCode = (code: string | null | undefined) => {
  if (!code) return null;
  return CURRENCY_TO_COUNTRY[code] ?? code.slice(0, 2).toUpperCase();
};

const toNumber = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined || value === "") return 0;
  const n = typeof value === "string" ? Number(value.replace(/,/g, "")) : value;
  return Number.isFinite(n) ? n : 0;
};

const formatNumber = (n: number) =>
  n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * A method's label from its key. The server's `label` is English, so it
 * showed on Arabic screens whenever the method had people in it (B53).
 */
const methodLabel = (t: TFunction, b: IPaymentMethodBreakdown): string => {
  switch (b.method) {
    case "WPS":
      return t("company:payrolls.wps");
    case "BANK_TRANSFER":
      return t("company:payrolls.bank_transfer");
    case "CASH":
      return t("company:payrolls.cash");
    case "CHEQUE":
      return t("company:payrolls.cheque");
    case "HELD":
      return t("company:payrolls.on_hold");
    case "UNASSIGNED":
      return t("company:payrolls.not_assigned");
    default:
      return b.label;
  }
};

const CurrencyFlag = ({ code }: { code: string | null | undefined }) => {
  const cc = toCountryCode(code);
  if (!cc) return null;
  return (
    <ReactCountryFlag
      svg
      countryCode={cc}
      style={{ width: "1em", height: "0.75em", borderRadius: "1px" }}
    />
  );
};

// accent is a CSS color string (e.g. "var(--color-primary)", "#6438ff")
type AccentString = string;

const CostBreakdownBar = ({ totals }: { totals: ITotalByCurrency[] }) => {
  const { t } = useTranslation(["company", "common"]);
  if (!totals.length) return null;

  const aggregate = totals.reduce(
    (acc, t) => {
      acc.net += toNumber(t.net);
      acc.deductions += toNumber(t.deductions);
      acc.employerCost += toNumber(t.employerCost);
      return acc;
    },
    { net: 0, deductions: 0, employerCost: 0 },
  );

  const totalBase = aggregate.net + aggregate.deductions;
  if (totalBase <= 0) return null;

  const netPct = (aggregate.net / totalBase) * 100;
  const dedPct = (aggregate.deductions / totalBase) * 100;
  const currencyCode = totals[0]?.currencyCode ?? "";

  return (
    <div className="flex items-center gap-3 border-y border-borderSecondary py-2.5 my-1">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 shrink-0">
        <span className="size-1.5 rounded-full bg-success" />
        {t("company:payrolls.net_short")}
        <span className="font-semibold text-gray-700 tabular-nums">
          {netPct.toFixed(1)}%
        </span>
      </div>
      <div
        className="flex h-2.5 flex-1 overflow-hidden rounded-full bg-gray-100"
        title={t("company:payrolls.total_cost_amount", {
          currency: currencyCode,
          amount: formatNumber(totalBase),
        })}
      >
        <div
          className="bg-success transition-[width] duration-300"
          style={{ width: `${netPct}%` }}
          title={t("company:payrolls.net_amount", {
            currency: currencyCode,
            amount: formatNumber(aggregate.net),
          })}
        />
        <div
          className="bg-danger transition-[width] duration-300"
          style={{ width: `${dedPct}%` }}
          title={t("company:payrolls.deductions_amount", {
            currency: currencyCode,
            amount: formatNumber(aggregate.deductions),
          })}
        />
      </div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 shrink-0">
        <span className="font-semibold text-gray-700 tabular-nums">
          {dedPct.toFixed(1)}%
        </span>
        {t("company:payrolls.deductions")}
        <span className="size-1.5 rounded-full bg-danger" />
      </div>
    </div>
  );
};

// Stat tile — mirrors AI Token Usage StatTile exactly
const StatTile = ({
  icon: Icon,
  label,
  totals,
  field,
  isDeduction,
  accent,
  note,
}: {
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  totals: ITotalByCurrency[];
  field: "gross" | "net" | "deductions" | "employerCost" | "statutoryEmployee";
  isDeduction?: boolean;
  accent: AccentString;
  note?: string;
}) => (
  <div className="rounded-sm border border-borderSecondary bg-white p-4 flex flex-col gap-2 hover:shadow-xs transition-all">
    <div className="flex items-center gap-2">
      <div
        className="size-8 rounded-sm flex items-center justify-center shrink-0"
        style={{
          backgroundColor: `color-mix(in srgb, ${accent} 10%, transparent)`,
        }}
      >
        <Icon size={15} style={{ color: accent }} />
      </div>
      <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
        {label}
      </p>
    </div>
    {totals.length === 0 ? (
      <p className="text-xl font-bold text-gray-300">—</p>
    ) : (
      totals.map((t) => (
        <div
          key={t.currencyCode ?? "none"}
          className="flex items-center gap-1.5"
        >
          <CurrencyFlag code={t.currencyCode} />
          <p
            className={cn(
              "text-xl font-bold tabular-nums truncate leading-none",
              isDeduction ? "text-danger" : "text-gray-900",
            )}
          >
            {t.currencyCode ?? ""} {formatNumber(toNumber(t[field]))}
          </p>
        </div>
      ))
    )}
    {note && <p className="text-[10px] text-muted-foreground">{note}</p>}
  </div>
);

const METHOD_ICONS: Record<string, React.ComponentType<{ size?: number }>> = {
  WPS: Building2,
  BANK_TRANSFER: CreditCard,
  CASH: Banknote,
  HELD: PauseCircle,
  UNASSIGNED: HelpCircle,
};

const METHOD_ACCENTS: Record<string, AccentString> = {
  WPS: "var(--color-info)",
  BANK_TRANSFER: "var(--color-primary)",
  CASH: "var(--color-success)",
  HELD: "var(--color-danger)",
  UNASSIGNED: "var(--color-info)",
};

const MethodCard = ({
  b,
  sharePercent,
}: {
  b: IPaymentMethodBreakdown;
  sharePercent: number;
}) => {
  const { t } = useTranslation(["company"]);
  const Icon = METHOD_ICONS[b.method] ?? HelpCircle;
  const accent = METHOD_ACCENTS[b.method] ?? "var(--color-info)";
  const hasValue = b.totals.length > 0;

  return (
    <div className="rounded-sm border border-borderSecondary bg-white p-4 flex flex-col gap-2 hover:shadow-xs transition-all">
      <div className="flex items-center gap-2">
        <div
          className="size-8 rounded-sm flex items-center justify-center shrink-0"
          style={{
            backgroundColor: `color-mix(in srgb, ${accent} 10%, transparent)`,
          }}
        >
          <Icon size={15} style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold truncate">
            {methodLabel(t, b)}
          </p>
          <p className="text-[10px] text-gray-400">
            {t("company:payrolls.employee_count", { count: b.employeeCount })}
          </p>
        </div>
      </div>
      {!hasValue ? (
        <p className="text-xl font-bold text-gray-300">—</p>
      ) : (
        b.totals.map((t) => (
          <div
            key={t.currencyCode ?? "none"}
            className="flex items-center gap-1.5"
          >
            <CurrencyFlag code={t.currencyCode} />
            <p className="text-xl font-bold tabular-nums text-gray-900 truncate leading-none">
              {t.currencyCode ?? ""} {formatNumber(t.net)}
            </p>
          </div>
        ))
      )}
      {/* share-of-total bar */}
      <div className="flex items-center gap-2 mt-auto">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${Math.min(100, Math.max(0, sharePercent))}%`,
              backgroundColor: accent,
            }}
          />
        </div>
        <span
          className="text-[10px] font-bold tabular-nums shrink-0 w-8 text-end"
          style={{ color: accent }}
        >
          {hasValue ? `${sharePercent.toFixed(0)}%` : "—"}
        </span>
      </div>
    </div>
  );
};

interface IProps {
  totals?: ITotalByCurrency[];
  breakdown?: IPaymentMethodBreakdown[];
}

const HIDDEN_METHODS = new Set(["CHEQUE"]);
const buildAlwaysShowMethods = (
  t: TFunction,
): Array<{ key: string; label: string }> => [
  { key: "WPS", label: "WPS" },
  { key: "BANK_TRANSFER", label: t("company:payrolls.bank_transfer") },
  { key: "CASH", label: t("company:payrolls.cash") },
];

export const SummaryAccordions = ({ totals = [], breakdown = [] }: IProps) => {
  const { t } = useTranslation(["company", "common"]);
  const [expanded, setExpanded] = useState(true);

  // A held salary sits in the On Hold card only (B49) — the server counts it
  // in its method too, which counted it twice across the cards.
  const withoutHeld = (
    b: IPaymentMethodBreakdown,
  ): IPaymentMethodBreakdown => ({
    ...b,
    employeeCount: Math.max(0, b.employeeCount - (b.heldCount ?? 0)),
    totals: b.totals.map((x) => ({
      ...x,
      net: toNumber(x.net) - toNumber(x.heldNet),
    })),
  });
  const byKey = new Map(breakdown.map((b) => [b.method, withoutHeld(b)]));
  const baseList: IPaymentMethodBreakdown[] = buildAlwaysShowMethods(t).map(
    (m) =>
      byKey.get(m.key) ?? {
        method: m.key,
        label: m.label,
        employeeCount: 0,
        heldCount: 0,
        totals: [],
      },
  );
  const extras = breakdown
    .filter(
      (b) =>
        !HIDDEN_METHODS.has(b.method) &&
        !buildAlwaysShowMethods(t).some((m) => m.key === b.method),
    )
    .map(withoutHeld);

  const heldTotalsMap = new Map<string, number>();
  const heldGrossMap = new Map<string, number>();
  let heldEmployeeCount = 0;
  for (const b of breakdown) {
    heldEmployeeCount += b.heldCount;
    for (const t of b.totals) {
      const code = t.currencyCode ?? "—";
      if (t.heldNet > 0) {
        heldTotalsMap.set(code, (heldTotalsMap.get(code) ?? 0) + t.heldNet);
      }
      if (toNumber(t.heldGross) > 0) {
        heldGrossMap.set(
          code,
          (heldGrossMap.get(code) ?? 0) + toNumber(t.heldGross),
        );
      }
    }
  }
  // The run's totals are what it pays now and leave held pay out; a held
  // salary is still owed, so the period's cost puts it back (B49).
  const costTotals = totals.map((x) => ({
    ...x,
    gross: toNumber(x.gross) + (heldGrossMap.get(x.currencyCode ?? "—") ?? 0),
  }));
  const heldCostNote =
    heldGrossMap.size > 0
      ? t("company:payrolls.total_cost_includes_held", {
          amount: Array.from(heldGrossMap.entries())
            .map(([code, amt]) =>
              `${code === "—" ? "" : code} ${formatNumber(amt)}`.trim(),
            )
            .join(" · "),
        })
      : undefined;
  const heldCell: IPaymentMethodBreakdown = {
    method: "HELD",
    label: t("company:payrolls.on_hold"),
    employeeCount: heldEmployeeCount,
    heldCount: 0,
    totals: Array.from(heldTotalsMap.entries()).map(([code, amt]) => ({
      currencyCode: code === "—" ? null : code,
      currencySymbol: null,
      net: amt,
      heldNet: amt,
    })),
  };

  const visibleBreakdown = [...baseList, ...extras, heldCell];
  const distributionTotal = visibleBreakdown.reduce(
    (sum, b) => sum + b.totals.reduce((s, t) => s + toNumber(t.net), 0),
    0,
  );

  return (
    <div className="relative rounded-sm border border-borderSecondary bg-linear-to-br from-primary-muted via-white to-background overflow-hidden">
      {/* top gradient bar */}
      <span className="absolute inset-x-0 top-0 h-1 bg-linear-to-r from-primary via-violet-500 to-pink-500" />

      {/* header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center justify-between w-full px-4 py-3 mt-1 border-b border-borderSecondary"
      >
        <div className="flex items-center gap-2">
          <div className="size-7 rounded-sm flex items-center justify-center bg-primary/10">
            <Wallet size={14} className="text-primary" />
          </div>
          <span className="text-sm font-bold text-gray-900">
            {t("company:payrolls.payroll_summary")}
          </span>
        </div>
        {expanded ? (
          <ChevronUp size={16} className="text-primary" />
        ) : (
          <ChevronDown size={16} className="text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="flex flex-col gap-5 p-4">
          {/* Cost Breakdown */}
          <div className="flex flex-col gap-2">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold px-0.5">
              {t("company:payrolls.cost_breakdown")}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatTile
                icon={DollarSign}
                label={t("company:payrolls.total_cost")}
                totals={costTotals}
                field="gross"
                accent="var(--color-primary)"
                note={heldCostNote}
              />
              <StatTile
                icon={TrendingUp}
                label={t("company:payrolls.net_pay")}
                totals={totals}
                field="net"
                accent="var(--color-success)"
              />
              <StatTile
                icon={TrendingDown}
                label={t("company:payrolls.deductions")}
                totals={totals}
                field="deductions"
                isDeduction
                accent="var(--color-danger)"
              />
              <StatTile
                icon={Building2}
                label={t("company:payrolls.contributions")}
                totals={totals}
                field="statutoryEmployee"
                accent="var(--color-info)"
              />
            </div>
            <CostBreakdownBar totals={totals} />
          </div>

          {/* Payment Distribution */}
          <div className="flex flex-col gap-2">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold px-0.5">
              {t("company:payrolls.payment_distribution")}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {visibleBreakdown.map((b) => {
                const methodNet = b.totals.reduce(
                  (s, t) => s + toNumber(t.net),
                  0,
                );
                const share =
                  distributionTotal > 0
                    ? (methodNet / distributionTotal) * 100
                    : 0;
                return <MethodCard key={b.method} b={b} sharePercent={share} />;
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
